import { negotiateLanguages } from 'fluent-langneg';

/**
 * The context includes some helpers to create formatter functions and to deal
 * with plurals, as well as the resolved `LocaleInfo`.
 */
class Context {
    constructor(locale) {
        this.locale = locale;
        if (Intl.PluralRules) {
            this.plural = new Intl.PluralRules(locale.formatter);
            this.ordinal = new Intl.PluralRules(locale.formatter, { type: "ordinal" });
        }
    }
    /**
     * This returns the appropriate plural rule for the provided `num`, according
     * to the `formatter` locale.
     * See [Intl.PluralRules](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/PluralRules)
     * for more details.
     */
    selectPlural(num) {
        return this.plural ? this.plural.select(num) : "other";
    }
    /**
     * This returns the appropriate ordinal rule for the provided `num`, according
     * to the `formatter` locale.
     * See [Intl.PluralRules](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/PluralRules)
     * for more details.
     */
    selectOrdinal(num) {
        return this.ordinal ? this.ordinal.select(num) : "other";
    }
    /**
     * This creates a reusable date formatting function with optional `options`, according
     * to the `formatter` locale.
     * See [Intl.DateTimeFormat](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/DateTimeFormat)
     * for more details.
     */
    createDateFormatter(options) {
        const formatter = new Intl.DateTimeFormat(this.locale.formatter, options);
        return date => formatter.format(date);
    }
    /**
     * This creates a reusable number formatting function with optional `options`, according
     * to the `formatter` locale.
     * See [Intl.NumberFormat](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat)
     * for more details.
     */
    createNumberFormatter(options) {
        const formatter = new Intl.NumberFormat(this.locale.formatter, options);
        return num => formatter.format(num);
    }
    /**
     * This creates a reusable formatting function for monetary values, with optional `options`, according
     * to the `formatter` locale.
     * The formatter will have `style: "currency"`, and will use the `currency` as
     * provided by the monetary value.
     * See [Intl.NumberFormat](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat)
     * for more details.
     */
    createMonetaryFormatter(options) {
        const formatterCache = {};
        return ({ currency, value }) => {
            let formatter = formatterCache[currency];
            if (!formatter) {
                formatter = formatterCache[currency] = new Intl.NumberFormat(this.locale.formatter, Object.assign(Object.assign({}, options), { style: "currency", currency }));
            }
            return formatter.format(value);
        };
    }
}
const proto = Context.prototype;
// define some minifier-friendly aliases that are used inside the generated
// translations
proto.p = proto.selectPlural;
proto.o = proto.selectOrdinal;
proto.d = proto.createDateFormatter;
proto.n = proto.createNumberFormatter;
proto.m = proto.createMonetaryFormatter;

function parseRequestedLanguages(languages) {
    if (Array.isArray(languages)) {
        return languages;
    }
    return acceptedLanguages(languages);
}
// Copied and adapted from: https://github.com/projectfluent/fluent.js/pull/262
function parseAcceptLanguageEntry(entry, index) {
    const langWithQ = entry.split(";").map(u => u.trim());
    let q = 1.0;
    if (langWithQ.length > 1) {
        const qVal = langWithQ[1].split("=").map(u => u.trim());
        if (qVal.length === 2 && qVal[0].toLowerCase() === "q") {
            const qn = Number(qVal[1]);
            q = isNaN(qn) ? 0.0 : qn;
        }
    }
    return { index, lang: langWithQ[0], q };
}
function acceptedLanguages(acceptLanguageHeader = "") {
    const tokens = acceptLanguageHeader
        .split(",")
        .map(t => t.trim())
        .filter(Boolean);
    const langsWithQ = tokens.map(parseAcceptLanguageEntry);
    // order by q descending, keeping the header order for equal weights
    langsWithQ.sort((a, b) => (a.q === b.q ? a.index - b.index : b.q - a.q));
    return langsWithQ.map(t => t.lang);
}

function defineLoader(messageIds, loaders, fallbackLocale = "template") {
    const availableLocales = Object.keys(loaders);
    return async (languages) => {
        const requestedLocales = parseRequestedLanguages(languages);
        const [resolvedLocale] = negotiateLanguages(requestedLocales, availableLocales, {
            defaultLocale: fallbackLocale,
            strategy: "lookup",
        });
        let formatterLocale;
        try {
            formatterLocale = new Intl.NumberFormat(requestedLocales).resolvedOptions().locale;
        }
        catch (_a) {
            formatterLocale = new Intl.NumberFormat(fallbackLocale).resolvedOptions().locale;
        }
        const locale = {
            requested: requestedLocales,
            loaded: resolvedLocale,
            formatter: formatterLocale,
        };
        const context = new Context(locale);
        const messageFile = await loaders[resolvedLocale]();
        const messages = messageFile.default(context);
        const intl = (key) => {
            if (typeof key === "string") {
                const fn = intl[convertIdentifier(key)];
                return fn();
            }
            const fn = intl[convertIdentifier(key.id)];
            return fn(key.params);
        };
        intl.context = context;
        for (const [idx, id] of messageIds.entries()) {
            intl[id] = messages[idx];
        }
        return intl;
    };
}

/**
 * Intl-codegen will convert message Ids with dashes or underscores to use
 * camelCase notation instead. If there is any need to replicate that functionality,
 * use this function.
 */
function convertIdentifier(str) {
    return str.replace(/[_-](\w|$)/g, (_, ch) => ch.toUpperCase());
}

export { convertIdentifier, defineLoader };
